/**
 * Friend Inbox update — pure state transition function.
 *
 * Elm-ish architecture: the update function is pure. It takes a model
 * and a message and returns a new model plus zero or more effects to
 * run. No side effects, no I/O, no time-dependent behavior.
 *
 * Every message handler is a separate case. The exhaustive switch is
 * statically type-checked via the `never` discriminator.
 */
import {
  applyDetailResponse,
  applyListResponse,
  beginLoad,
  beginTransition,
  clearSelection,
  endTransition,
  initialFriendInboxModel,
  recordError,
  type FriendInboxModel,
} from './model';
import type { FriendInboxMsg } from './messages';
import type { FriendInboxEffect } from './effects';

/**
 * Effect produced by update. The effect runner consumes these; the
 * update function itself does not perform any side effect.
 */
export type { FriendInboxEffect };

/**
 * Result of a single update call: the new model and the effects the
 * runner should fire.
 */
export interface FriendInboxUpdateResult {
  readonly model: FriendInboxModel;
  readonly effects: ReadonlyArray<FriendInboxEffect>;
}

/**
 * Apply a single message to the model. Pure.
 */
export function update(
  model: FriendInboxModel,
  msg: FriendInboxMsg,
): FriendInboxUpdateResult {
  switch (msg.type) {
    case 'LoadRequested': {
      const next = beginLoad(model);
      return {
        model: next,
        effects: [{ type: 'ListInbox', token: next.refreshToken }],
      };
    }

    case 'LoadSucceeded':
      // Discard stale responses (token mismatch).
      if (msg.token !== model.refreshToken) {
        return { model, effects: [] };
      }
      return { model: applyListResponse(model, msg.response), effects: [] };

    case 'LoadFailed':
      if (msg.token !== model.refreshToken) {
        return { model, effects: [] };
      }
      return {
        model: { ...model, load: 'error', error: msg.error },
        effects: [],
      };

    case 'ItemSelected':
      return {
        model,
        effects: [{ type: 'GetIntro', id: msg.id, token: model.refreshToken }],
      };

    case 'ItemSelectedSucceeded':
      return { model: applyDetailResponse(model, msg.detail), effects: [] };

    case 'ItemSelectedFailed':
      return { model: recordError(model, msg.error), effects: [] };

    case 'SelectionCleared':
      return { model: clearSelection(model), effects: [] };

    case 'AcceptRequested':
      return {
        model: beginTransition(model, 'accept'),
        effects: [
          { type: 'AcceptIntro', id: msg.id, token: model.refreshToken },
        ],
      };

    case 'RejectRequested':
      return {
        model: beginTransition(model, 'reject'),
        effects: [
          { type: 'RejectIntro', id: msg.id, token: model.refreshToken },
        ],
      };

    case 'TransitionSucceeded':
      return { model: endTransition(model, msg.detail), effects: [] };

    case 'TransitionFailed':
      return { model: recordError(model, msg.error), effects: [] };

    case 'RefreshRequested': {
      const next = beginLoad(model);
      return {
        model: next,
        effects: [{ type: 'ListInbox', token: next.refreshToken }],
      };
    }

    default: {
      const _exhaustive: never = msg;
      void _exhaustive;
      return { model, effects: [] };
    }
  }
}

/**
 * Convenience: build an initial program state and run an initial
 * message (LoadRequested) to kick off the inbox fetch.
 */
export function initialUpdateResult(): FriendInboxUpdateResult {
  return update(initialFriendInboxModel(), { type: 'LoadRequested' });
}
